import { Checkbox, Group, Text, useMantineTheme } from '@mantine/core';
import { UseBuildColumnsReturn } from '@override';
import { IconBell } from '@tabler/icons-react';

import { BatchTypeEnum } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { TableNotificationIndicator } from '@components';
import { VIEW_DETAILS } from '@constants';
import { BatchesActions } from '@enums';
import { buildColumnsActions, useBuildInformationColumns, useBuildRiskAnalysisColumns, useBuildSendingColumns } from '@features/batches/BatchListColumns';
import { BatchStatusIndicator } from '@features/batches/BatchStatusIndicator';
import { BulkActionsMenu } from '@features/batches/BulkActionsMenu';
import { useRetrieveDeclarationByNotification } from '@hooks/batch/retrieve';
import { useSetImmediateResponse } from '@hooks/batch/simulator';
import { useProgress } from '@hooks/ProgressContext';
import { useUser } from '@hooks/user';

export const useBuildColumns = ({
  columnsDropdownOptions,
  handleTableAction,
  declarant,
  settings,
  batchView,
  selectedBatchIds = [],
  handleSelectBatch,
  handleSelectAllBatches,
  allBatchIds = [],
  batchesData,
  clearSelectedBatches,
}): UseBuildColumnsReturn => {
  const sendingColumns = useBuildSendingColumns({ settings, batchView });
  const informationsColumns = useBuildInformationColumns();
  const riskAnalysisColumns = useBuildRiskAnalysisColumns({ settings, batchView });

  const { progress } = useProgress();
  const { data: user } = useUser();
  const theme = useMantineTheme();
  const setImmediateResponse = useSetImmediateResponse();
  const retrieveDeclarationByNotification = useRetrieveDeclarationByNotification();

  return [
    {
      name: (
        <Checkbox
          checked={allBatchIds.length > 0 && selectedBatchIds.length === allBatchIds.length}
          indeterminate={selectedBatchIds.length > 0 && selectedBatchIds.length < allBatchIds.length}
          onChange={(e) => handleSelectAllBatches(e.target.checked, allBatchIds)}
        />
      ),
      cell: (row) => <Checkbox checked={selectedBatchIds.includes(row._id)} onChange={(e) => handleSelectBatch(row._id, e.target.checked)} />,
      width: '20px',
    },
    {
      name: <IconBell />,
      cell: (row) => (
        <TableNotificationIndicator
          actionsPending={row.statistics.actionsPending}
          notificationsPending={row.statistics.notificationsPending}
          comments={!!row.commentsCount}
          navigateTo={(filterType) => retrieveDeclarationByNotification.mutateAsync({ batchId: row._id, filterType })}
        />
      ),
      hide: batchView === BatchTypeEnum.LOW_VALUES,
    },
    {
      name: 'Status',
      width: '80px',
      cell: (row) => <BatchStatusIndicator key={`status-indicator-${row._id}`} batch={row} />,
    },
    {
      name: 'Batch ID',
      selector: 'counter',
      width: '120px',
      cell: (row) => row.counter,
      sortable: true,
    },
    {
      name: 'Forwarder',
      selector: 'forwarder.name',
      width: '120px',
      cell: (row) => (row as any).forwarder?.name,
      sortable: true,
      hide: !settings.walletEnabled,
    },
    {
      name: 'Total decl.',
      selector: 'statistics.declarationGroups',
      sortable: true,
      width: '50px',
      cell: (row, rowIdx) => <div data-pw={`cell-total-${rowIdx}`}>{row.statistics.declarationGroups || 0}</div>,
      hide: batchView === BatchTypeEnum.LOW_VALUES,
    },
    {
      name: batchView === BatchTypeEnum.LOW_VALUES ? 'Total decl.' : 'Total lines',
      selector: 'statistics.total',
      sortable: true,
      width: '50px',
      cell: (row) => <div data-pw={`cell-total-${row.name}`}>{row.statistics.total}</div>,
    },
    ...(columnsDropdownOptions.riskAnalysis.checked ? riskAnalysisColumns : []),
    ...(columnsDropdownOptions.sending?.checked ? sendingColumns : []),
    ...(columnsDropdownOptions.fileName.checked ? informationsColumns.fileName : []),
    ...(columnsDropdownOptions.batchName.checked ? informationsColumns.batchName : []),
    ...(columnsDropdownOptions.customer.checked ? informationsColumns.customer : []),
    ...(columnsDropdownOptions.date.checked ? informationsColumns.date : []),
    ...(columnsDropdownOptions.templateName.checked ? informationsColumns.templateName : []),
    ...(columnsDropdownOptions.numberOfUpdates.checked ? informationsColumns.numberOfUpdates : []),
    {
      name: (
        <Group justify="space-between" align="center">
          <Text>Actions</Text>
          {selectedBatchIds.length > 0 && (
            <BulkActionsMenu selectedBatchIds={selectedBatchIds} batchesData={batchesData} batchView={batchView} onBulkActionDone={clearSelectedBatches} />
          )}
        </Group>
      ),
      sticky: 'right',
      cell: (row, index) => {
        const tableActionsList: { icon: string; text: string; onClick: () => void }[] = [];
        const secondaryActionsList = [
          {
            icon: IconFactory.view,
            text: VIEW_DETAILS,
            onClick: () => handleTableAction(BatchesActions.VIEW, row),
          },
        ];

        return buildColumnsActions({
          handleTableAction,
          declarant,
          settings,
          batchView,
          row,
          index,
          tableActionsList,
          secondaryActionsList,
          progress,
          user,
          theme,
          setImmediateResponse,
        });
      },
    },
  ];
};
